import { IconExternalLink } from "@tabler/icons-react"
import { type FC, useState } from "react"
import { Navigate, useParams } from "react-router"
import styled from "styled-components"

import { toDateTimeString } from "@forento/core/utilities/date"
import { parseNumber } from "@forento/core/utilities/number"
import CommunityProfileAvatar from "@forento/ui/components/CommunityProfileAvatar"
import { tw } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import Layout, { PageBreadcrumb, PageHeader } from "~/components/Layout"
import Link from "~/components/Link"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { usePlatform } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import Course from "./Course"
import DeleteModal from "./DeleteModal"
import Downloadable from "./Downloadable"
import Event from "./Event"
import PageInput from "./PageInput"
import Video from "./Video"

const UserDetailPage: FC = () => {
	const userId = parseNumber(useParams().userId ?? "") ?? -1
	const platform = usePlatform()!

	const { data: user, error, refetch } = query.user.getDetailedUserById.useQuery(userId)

	const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)

	if (user === null) return <Navigate to={routes.user.index()} />

	const statLabelClass = tw`text-xs font-semibold tracking-wider text-slate-500 uppercase`

	const sectionContentClass = tw`flex flex-col gap-4 p-6`
	const sectionCountClass = tw`bg-primary min-w-6 rounded-xl px-3 py-1 text-center text-sm font-semibold text-white`

	return (
		<Layout>
			<PageHeader>
				<PageBreadcrumb
					path={[{ title: "Users", link: routes.user.index() }]}
					title={!user ? "..." : `${user.firstName} ${user.lastName}`}
				/>
				{!error && user && (
					<Button variant="primary-danger" onClick={() => setDeleteModalOpen(true)}>
						Delete
					</Button>
				)}
			</PageHeader>
			{error ? (
				<p className="text-danger font-medium">Failed to load user.</p>
			) : !user ? (
				<PartialLoadingPage />
			) : (
				<div className="flex w-full flex-col gap-8">
					<DeleteModal
						isOpen={isDeleteModalOpen}
						user={{
							id: user.id,
							hasMembership: user.membership !== null,
							hasPurchasedContent: [...user.courses, ...user.downloadables, ...user.videos].some(
								x => x.status === "unlocked" && x.unlockedCause === "purchased",
							),
						}}
						onClose={() => setDeleteModalOpen(false)}
						onDeleted={refetch}
					/>

					<ProfileCard>
						<Avatar profile={user} />
						<div className="min-w-0 flex-[1_1_240px] break-words">
							<h1 className="mb-3 text-2xl font-bold">
								{user.firstName} {user.lastName}
							</h1>
							<div className="mb-6 font-medium text-slate-600">{user.email}</div>
							<div className="flex flex-wrap gap-6">
								<div className="flex flex-col gap-1">
									<div className={statLabelClass}>Member since</div>
									<div className="text-sm font-semibold">{toDateTimeString(user.registerDate)}</div>
								</div>
								{user.phoneNumber && (
									<div className="flex flex-col gap-1">
										<div className={statLabelClass}>Phone</div>
										<div className="text-sm font-semibold">{user.phoneNumber}</div>
									</div>
								)}
								<div className="flex flex-col gap-1">
									<div className={statLabelClass}>Membership</div>
									<div className="text-sm font-semibold">{user.membership?.title ?? "None"}</div>
								</div>
								{platform.community && (
									<div className="flex flex-col gap-1">
										<div className={statLabelClass}>Community</div>
										{user.communityProfileId ? (
											<Link
												to={routes.community.profile(user.communityProfileId)}
												className="hover:text-primary flex cursor-pointer items-center gap-1 text-sm font-semibold transition-colors"
											>
												<IconExternalLink className="size-4" />
												Visit community profile
											</Link>
										) : (
											<div className="text-sm font-semibold">Not registered</div>
										)}
									</div>
								)}
							</div>
						</div>
					</ProfileCard>

					<div className="flex flex-col gap-8">
						{user.courses.length > 0 && (
							<ContentSection>
								<SectionHeader>
									<h2 className="text-lg font-semibold">Courses</h2>
									<div className={sectionCountClass}>{user.courses.length}</div>
								</SectionHeader>
								<div className={sectionContentClass}>
									{user.courses.map(course => (
										<Course key={course.id} course={course} userId={user.id} reload={refetch} />
									))}
								</div>
							</ContentSection>
						)}

						{user.videos.length > 0 && (
							<ContentSection>
								<SectionHeader>
									<h2 className="text-lg font-semibold">Videos</h2>
									<div className={sectionCountClass}>{user.videos.length}</div>
								</SectionHeader>
								<div className={sectionContentClass}>
									{user.videos.map(video => (
										<Video key={video.id} video={video} userId={userId} reload={refetch} />
									))}
								</div>
							</ContentSection>
						)}

						{user.downloadables.length > 0 && (
							<ContentSection>
								<SectionHeader>
									<h2 className="text-lg font-semibold">Downloadables</h2>
									<div className={sectionCountClass}>{user.downloadables.length}</div>
								</SectionHeader>
								<div className={sectionContentClass}>
									{user.downloadables.map(downloadable => (
										<Downloadable
											key={downloadable.id}
											downloadable={downloadable}
											userId={userId}
											reload={refetch}
										/>
									))}
								</div>
							</ContentSection>
						)}

						{user.events.length > 0 && (
							<ContentSection>
								<SectionHeader>
									<h2 className="text-lg font-semibold">Events</h2>
									<div className={sectionCountClass}>{user.events.length}</div>
								</SectionHeader>
								<div className={sectionContentClass}>
									{user.events.map(event => (
										<Event key={event.id} event={event} />
									))}
								</div>
							</ContentSection>
						)}

						{user.inputs.length > 0 && (
							<ContentSection>
								<SectionHeader>
									<h2 className="text-lg font-semibold">Course inputs</h2>
									<div className={sectionCountClass}>{user.inputs.length}</div>
								</SectionHeader>
								<div className={sectionContentClass}>
									{user.inputs.map(input => (
										<PageInput key={input.id} page={input} chapterTitle={input.chapter.title} />
									))}
								</div>
							</ContentSection>
						)}
					</div>
				</div>
			)}
		</Layout>
	)
}

const ProfileCard = styled.div`
	background: #f9f5ff;
	border-radius: 24px;
	padding: 32px;
	color: #1a1a1a;
	display: flex;
	align-items: flex-start;
	gap: 24px;
	flex-wrap: wrap;
`

const Avatar = styled(CommunityProfileAvatar).attrs({ size: 80 })`
	box-sizing: content-box;
	border: 4px solid rgba(255, 255, 255, 0.2);
	background-color: rgba(255, 255, 255, 0.1);
	flex-shrink: 0;
`

const ContentSection = styled.div`
	background: white;
	border-radius: 24px;
	border: 1px solid var(--color-component-border);
	overflow: hidden;
`

const SectionHeader = styled.div`
	background: var(--color-component-background);
	padding: 20px 24px;
	border-bottom: 1px solid var(--color-component-border);
	display: flex;
	align-items: center;
	justify-content: space-between;
`

export default UserDetailPage
